import { use } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import Link from '@mui/icons-material/Link';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import Divider from 'shared/components/divider/divider';
import SelectField from 'shared/components/formFields/select-field';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { DeviceType } from 'shared/models/device-type';

import useIPUnitDoorEntrances, {
  IPUnitDoorEntranceField,
} from 'features/systems/ip-unit/components/ip-unit-door-entrances/hooks/use-ip-unit-door-entrances';
import IPUnitDoorEntrance from 'features/systems/ip-unit/domain/models/ip-unit-door-entrance';
import IPUnitProviderContext from 'features/systems/ip-unit/providers/contexts/ip-unit-provider-context';
import styles from './ip-unit-door-entrances.module.scss';

const IPUnitDoorEntrances = (): React.JSX.Element => {
  const { t } = useTranslation('ipUnit', { keyPrefix: 'doorEntrance' });
  const { t: tDeviceTypes } = useTranslation('deviceTypes');
  const { hasPermission } = usePermissionAuthorization();
  const {
    viewingMode,
    openDeviceDetailsByExternalId,
    getUnitLocationDeviceBreadCrumbs,
    unitLocationHasLinkedDevice,
    unitLocationHasDevice,
  } = use(IPUnitProviderContext);
  const { control } = useFormContext();
  const { doorEntrances, addDoorEntrance, deleteDoorEntrance, createDevice } = useIPUnitDoorEntrances();
  const watchedDoorEntrances: IPUnitDoorEntrance[] = useWatch({
    control,
    name: 'doorEntrances',
  });

  const getExternalId = (doorEntrance: IPUnitDoorEntrance): string =>
    `${tDeviceTypes(DeviceType.DoorAlarm).toLocaleLowerCase()}_${doorEntrance.number}`;

  const renderDoorColumn = (item: IPUnitDoorEntrance, index: number): React.JSX.Element => {
    const maximumDoorEntrances = 9;

    return viewingMode === 'editing' ? (
      <Controller
        control={control}
        name={`doorEntrances.${index}.number`}
        rules={{ required: t('info.requiredHelperText') }}
        render={({ field, fieldState: { error } }) => (
          <SelectField
            {...field}
            value={field.value ?? ''}
            error={!!error}
            id={`${item.number}`}
            disabled={unitLocationHasDevice(getExternalId(item))}
            label={t('door')}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
          >
            {Array.from({ length: maximumDoorEntrances }, (_, menuItemIndex) => {
              const value = menuItemIndex + 1;
              const isSelectedElsewhere = watchedDoorEntrances.some((entrance, i) => i !== index && entrance.number === value);
              if (isSelectedElsewhere) {
                return null;
              }

              return (
                <MenuItem key={value} value={value}>
                  {t('door')} {value}
                </MenuItem>
              );
            })}
          </SelectField>
        )}
      />
    ) : (
      <Stack direction="row" className={styles.deviceIconWrapper}>
        <Typography
          variant="subtitle1"
          className={`${unitLocationHasDevice(getExternalId(item)) ? styles.clickable : ''} `}
          onClick={() => openDeviceDetailsByExternalId(getExternalId(item))}
        >
          {t('door')} {item.number}
        </Typography>
        {unitLocationHasLinkedDevice(getExternalId(item)) && (
          <BreadCrumbTooltip
            allElementNames={getUnitLocationDeviceBreadCrumbs(`${tDeviceTypes(DeviceType.DoorAlarm).toLocaleLowerCase()}_${item.number}`)}
          >
            <Link className={styles.linkIcon} />
          </BreadCrumbTooltip>
        )}
      </Stack>
    );
  };

  const renderActionsColumn = (doorEntrance: IPUnitDoorEntrance, index: number): React.JSX.Element => {
    if (viewingMode === 'viewing' && hasPermission(Permission.CreateDevice) && !unitLocationHasDevice(getExternalId(doorEntrance))) {
      return (
        <Button startIcon={<AddCircleOutlineRounded color="primary" />} variant="text" onClick={() => createDevice(doorEntrance)}>
          {t('createDevice')}
        </Button>
      );
    }

    if (viewingMode === 'editing') {
      return (
        <IconButton
          className={styles.cancelButton}
          onClick={() => deleteDoorEntrance(index)}
          disabled={unitLocationHasDevice(getExternalId(doorEntrance))}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>
      );
    }

    return <></>;
  };

  const columns: ColumnDefinition<IPUnitDoorEntrance, NestedKeyof<IPUnitDoorEntrance>>[] = [
    {
      key: 'number',
      label: t('door'),
      disableSort: true,
      renderCustomContentProvider: (item, index) => renderDoorColumn(item, index),
      tableCellProps: {
        width: '300px',
      },
    },
    {
      key: 'id',
      label: t('events'),
      disableSort: true,
      renderCustomContentProvider: (item) => (
        <div className={styles.events}>
          <Typography variant="subtitle1">
            {t('open')}: {unitLocationHasDevice(getExternalId(item)) ? t('openFunction') : '-'}
          </Typography>
          <Typography variant="subtitle1">
            {t('closed')}: {unitLocationHasDevice(getExternalId(item)) ? t('closedFunction') : '-'}
          </Typography>
        </div>
      ),
    },
    {
      key: 'keyId',
      label: '',
      disableSort: true,
      renderCustomContentProvider: (item, index) => renderActionsColumn(item, index),
      tableCellProps: {
        align: 'right',
      },
    },
  ];

  return (
    <div className={styles.ipUnitDoorEntranceContainer}>
      <Typography className={styles.locationsTitle} variant="h5">
        {t('title')}
      </Typography>

      {doorEntrances.length > 0 ? (
        <Table
          className={styles.table}
          columns={columns}
          data={doorEntrances.map<RowItem<IPUnitDoorEntranceField>>((item) => ({ data: item, showColorOnHover: false }))}
          rowIdentifier={(item) => item?.keyId}
        ></Table>
      ) : (
        <Typography variant="subtitle1">{t('noDoorEntrances')}</Typography>
      )}
      {viewingMode === 'editing' && (
        <div className={styles.addDoorEntranceContainer}>
          <Button
            startIcon={<AddCircleOutlineRounded color="primary" className={doorEntrances.length >= 9 ? styles.disabled : ''} />}
            onClick={addDoorEntrance}
            disabled={doorEntrances.length >= 9}
          >
            {t('addDoorEntrance')}
          </Button>
        </div>
      )}
      {doorEntrances.length === 0 && <Divider className={styles.divider} />}
    </div>
  );
};

export default IPUnitDoorEntrances;
